import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  HostListener
} from '@angular/core';
import { Router } from '@angular/router';
import { Observable, interval, Subject } from 'rxjs';
import { map, takeUntil, startWith } from 'rxjs/operators';
import { Reminder, ReminderType } from '../../models/reminder.model';
import { getTypeIcon, getTypeColor, getTypeBgColor } from '../../utils/icon-mapping.util';
import { RemindersService } from '../../services/reminders.service';

interface SnoozeDuration {
  label: string;
  value: number;
}

interface ReminderStatusInfo {
  icon: string;
  cssClass: string;
  tooltip: string;
}

@Component({
    selector: 'app-reminder-card',
    templateUrl: './reminder-card.component.html',
    styleUrls: ['./reminder-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ReminderCardComponent implements OnInit, OnDestroy, OnChanges {
  @Input() reminder!: Reminder;
  @Input() snoozeOpen = false;

  @Output() onSnooze = new EventEmitter<{ id: string | number; duration: number }>();
  @Output() onDismiss = new EventEmitter<string | number>();
  @Output() onComplete = new EventEmitter<string | number>();
  @Output() onSnoozeToggle = new EventEmitter<string | number | null>();
  @Output() onEdit = new EventEmitter<Reminder>();
  @Output() onNavigate = new EventEmitter<void>();

  @ViewChild('snoozeBtn', { read: ElementRef }) snoozeBtnRef!: ElementRef;

  snoozeDurations: SnoozeDuration[] = [
    { label: '5 min', value: 5 },
    { label: '10 min', value: 10 },
    { label: '15 min', value: 15 },
    { label: '30 min', value: 30 },
    { label: '1 hour', value: 60 },
    { label: '2 hours', value: 120 },
    { label: '4 hours', value: 240 },
    { label: '8 hours', value: 480 },
    { label: '1 day', value: 1440 },
    { label: '2 days', value: 2880 },
    { label: '1 week', value: 10080 }
  ];

  dynamicDueIn$!: Observable<string>;
  snoozeDropdownPosition = { top: 0, left: 0 };
  private destroy$ = new Subject<void>();

  constructor(
    private remindersService: RemindersService,
    private router: Router
  ) {}

  /**
   * Status indicator shown at the top-right of the title row — one per status.
   * Getter (not cached) so it stays live as the reminder flips pending → overdue.
   */
  get statusInfo(): ReminderStatusInfo {
    const r = this.reminder;
    if (r?.status === 'completed' || r?.completed) {
      return { icon: 'check_circle', cssClass: 'status-completed', tooltip: 'Completed' };
    }
    if (r?.status === 'cancelled') {
      return { icon: 'cancel', cssClass: 'status-cancelled', tooltip: 'Cancelled' };
    }
    if (r?.status === 'snoozed') {
      return { icon: 'snooze', cssClass: 'status-snoozed', tooltip: 'Snoozed' };
    }
    if (r?.overdue) {
      return { icon: 'error', cssClass: 'status-overdue', tooltip: 'Overdue' };
    }
    return { icon: 'schedule', cssClass: 'status-pending', tooltip: 'Pending' };
  }

  /**
   * Navigate to the linked customer (proposal reminders).
   */
  navigateToCustomer(event: MouseEvent): void {
    event.stopPropagation();
    const id = this.reminder?.reminderable?.customer_id;
    if (!id) {
      return;
    }
    this.router.navigate(['/customers/customer', id]);
    this.onNavigate.emit();
  }

  /**
   * Navigate to the linked bid (proposal reminders).
   */
  navigateToBid(event: MouseEvent): void {
    event.stopPropagation();
    const id = this.reminder?.reminderable?.bid_id;
    if (!id) {
      return;
    }
    this.router.navigate(['/bids/bid', id]);
    this.onNavigate.emit();
  }

  ngOnInit(): void {
    this.setupDynamicCountdown();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Recreate countdown observable when reminder changes (e.g., after update)
    if (changes['reminder'] && !changes['reminder'].firstChange) {
      this.setupDynamicCountdown();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupDynamicCountdown(): void {
    // Determine update frequency based on time remaining
    const minutesRemaining = this.remindersService.getMinutesRemaining(this.reminder);
    const updateInterval = minutesRemaining <= 5 ? 1000 : 60000; // 1 sec if ≤5 min, else 1 min

    // Create dynamic countdown observable
    this.dynamicDueIn$ = interval(updateInterval).pipe(
      startWith(0),
      map(() => this.remindersService.calculateDueIn(this.reminder)),
      takeUntil(this.destroy$)
    );
  }

  /**
   * Build a Date from the stored reminder_date (Y-m-d) + reminder_time (HH:mm[:ss]),
   * so the template can format it with the shared `date` pipe convention.
   */
  get reminderDateTime(): Date | null {
    const datePart = this.reminder?.reminder_date || this.reminder?.date;
    const timePart = this.reminder?.reminder_time || this.reminder?.time;
    if (!datePart) {
      return null;
    }
    const dt = new Date(`${datePart}T${timePart || '00:00'}`);
    return isNaN(dt.getTime()) ? null : dt;
  }

  handleComplete(): void {
    this.onComplete.emit(this.reminder.id);
  }

  handleSnoozeToggle(): void {
    if (!this.snoozeOpen && this.snoozeBtnRef) {
      // Calculate dropdown position when opening
      const btnRect = this.snoozeBtnRef.nativeElement.getBoundingClientRect();
      const dropdownHeight = 300; // Max height of dropdown
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - btnRect.bottom;
      const spaceAbove = btnRect.top;

      // Position above button if not enough space below
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        this.snoozeDropdownPosition = {
          top: btnRect.top - dropdownHeight - 4, // 4px above button
          left: btnRect.left
        };
      } else {
        this.snoozeDropdownPosition = {
          top: btnRect.bottom + 4, // 4px below button
          left: btnRect.left
        };
      }
    }
    this.onSnoozeToggle.emit(this.snoozeOpen ? null : this.reminder.id);
  }

  // Close dropdown when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.snoozeOpen && this.snoozeBtnRef) {
      const clickedInsideButton = this.snoozeBtnRef.nativeElement.contains(event.target);
      const clickedInsideDropdown = (event.target as HTMLElement).closest('.snooze-dropdown-overlay');

      if (!clickedInsideButton && !clickedInsideDropdown) {
        this.onSnoozeToggle.emit(null); // Close dropdown
      }
    }
  }

  handleSnooze(duration: number): void {
    this.onSnooze.emit({ id: this.reminder.id, duration });
  }

  handleDismiss(): void {
    this.onDismiss.emit(this.reminder.id);
  }

  handleEdit(): void {
    this.onEdit.emit(this.reminder);
  }

  getTypeIcon(type: ReminderType): string {
    return getTypeIcon(type);
  }

  getTypeColorClass(type: ReminderType): string {
    return getTypeColor(type);
  }

  getTypeBgColorClass(type: ReminderType): string {
    return getTypeBgColor(type);
  }

  getIconContainerClasses(): string {
    const type = this.reminder.type;
    const baseClasses = 'reminder-icon-container';

    switch (type) {
      case 'meeting':
        return `${baseClasses} icon-meeting`;
      case 'task':
        return `${baseClasses} icon-task`;
      case 'event':
        return `${baseClasses} icon-event`;
      case 'deadline':
        return `${baseClasses} icon-deadline`;
      case 'alert':
        return `${baseClasses} icon-alert`;
      default:
        return `${baseClasses} icon-default`;
    }
  }

  isUrgent(): boolean {
    return this.remindersService.isReminderUrgent(this.reminder);
  }
}
